import { addDays, startOfDay } from "date-fns";
import SearchForm from "../components/SearchForm";
import { useEffect, useState } from "react";
import { Resort, SearchParams, YadoHotel, YadoRoom } from "../types";
import { formatQuery, parseUrlParamsToQuery, roomPlanPrices } from "../utils";
import { fetchHotels, fetchHotelsWithoutDate } from "../api";
import SearchFormFiltering from "../components/SearchFormFiltering";
import HotelList from "./HotelList";
import checkPointSvg from '../images/checkPoint.svg';
import treeSvg from '../images/tree.svg';
import { getImage } from "../../utils/pathHelper";
import i18n from "../../i18n";
import styles from "../styles/HotelSearch.module.scss";
import SelectBoxSort from "../components/SelectBoxSort";
import ResortFilteringBar from "./ResortFilteringBar";
import BreadCrumb from "../../components/BreadCrumb";
import { isEqual } from "../../utils/arrayHelper";
import SearchFormFilteringSp from "../components/SearchFormFilteringSp";

type HotelSearchProps = {
  featuredAreaCodes: string[];
  otherAreaCodes: string[];
}

const HotelsSearch: React.FC<HotelSearchProps> = ({ featuredAreaCodes, otherAreaCodes }) => {
  const params = new URLSearchParams(window.location.search);
  const defaultResortCode = params.get("resortCode");
  const forHoshinoResort = params.get("hoshinoResort");
  const today = new Date();
  const fourtyFiveDaysLater = startOfDay(addDays(today, 45));
  const defaultCheckoutDate = forHoshinoResort ? addDays(fourtyFiveDaysLater, 2) : addDays(fourtyFiveDaysLater, 1);
  const recommendedHotelsHTML = window.reactProps.recommendedHotelsHTML

  const defaultQuery: SearchParams = {
    checkinDate: null,
    checkoutDate: null,
    people: { adult: 2, children: [] },
    areaCodes: [],
    resortCodes: [],
    skiInOut: null,
    hotSpring: null,
  };

  const initialQuery = parseUrlParamsToQuery(window.location.search, defaultQuery);

  const [fetchedHotels, setFetchedHotels] = useState<YadoHotel[]>([]);
  const [fetchedQuery, setFetchedQuery] = useState<SearchParams>(initialQuery);
  const [hotels, setHotels] = useState<YadoHotel[]>([]);
  const [relatedResorts, setRelatedResorts] = useState<Resort[]>([]);
  const [query, setQuery] = useState<SearchParams>(initialQuery);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchInitiated, setSearchInitiated] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('featured');

  useEffect(() => {
    updateHotels();
  }, []);

  useEffect(() => {
    setHotels(sortHotels(sortBy));
  }, [sortBy]);

  useEffect(() => {
    filterHotelsByResort(query.resortCodes);
  }, [query.resortCodes]);

  const updateHotels = async (currentQuery: SearchParams = query) => {
    setIsLoading(true);
    setSearchInitiated(true);
    const isAreaChanged = isEqual(currentQuery.areaCodes, fetchedQuery.areaCodes)
    if (!isAreaChanged) {
      // エリアが変更された場合、リゾートコードの選択をリセット
      setQuery({ ...query, resortCodes: [] })
    }
    const formattedQuery = formatQuery(currentQuery);
    try {
      const dateSelected = query.checkinDate && query.checkoutDate;
      const hotels = dateSelected ? await fetchHotels(formattedQuery) : await fetchHotelsWithoutDate(formattedQuery);
      setIsLoading(false);
      setHotels(sortHotels(sortBy, hotels));
      setFetchedHotels(hotels)
      setFetchedQuery(currentQuery)
      if (defaultResortCode) {
        const sortedRelatedResorts = extractRelatedResorts(hotels).sort((a, _b) => a.resort_code === defaultResortCode ? -1 : 1)
        setRelatedResorts(sortedRelatedResorts)
        setQuery({ ...query, resortCodes: [defaultResortCode] })
      } else {
        setRelatedResorts(extractRelatedResorts(hotels))
      }
    } catch {
      setIsLoading(false);
      setHotels([])
      setFetchedHotels([])
      setRelatedResorts([])
    }
  };

  const handleClickHotelCardArea = (e, areaCode) => {
    e.stopPropagation();
    const newQuery = { ...query, areaCodes: [areaCode] };
    setQuery(newQuery);
    updateHotels(newQuery);
  };

  const handleOnChangeFiltering = (query) => {
    setQuery(query)
    updateHotels(query)
  }

  const sortHotels = (sort: string, targetHotels: YadoHotel[] = hotels) => {
    const sortedHotels = [...targetHotels];
    if (sort === 'featured') {
      sortedHotels.sort((a, b) => b.priority - a.priority);

    } else if (sort === 'priceAsc') {
      sortedHotels.sort((a, b) =>  {
        return Math.min(...roomPlanPrices(findRoomWithMinPrice(a.yado_rooms))) -  Math.min(...roomPlanPrices(findRoomWithMinPrice(b.yado_rooms)))
    });
    } else if (sort === 'priceDesc') {
      sortedHotels.sort((a, b) => {
        return (
          Math.max(...roomPlanPrices(findRoomWithMaxPrice(b.yado_rooms))) -
          Math.max(...roomPlanPrices(findRoomWithMaxPrice(a.yado_rooms)))
        );
      });
    }

    return sortedHotels;
  };

  const filterHotelsByResort = (resortCodes: string[]) => {
    const hotelsFilteredByResort = fetchedHotels.filter(hotel => {
      return (
        query.resortCodes.length === 0 ||
        hotel.resorts.some(resort =>
          resortCodes.some(code => code === resort.resort_code)
        )
      );
    });
    setHotels(sortHotels(sortBy, hotelsFilteredByResort));
  };

  const extractRelatedResorts = hotels => {
    const eachResortsCount = hotels
      .flatMap(hotel => hotel.resorts)
      .reduce((acc, resort) => {
        acc[resort.resort_code] = (acc[resort.resort_code] || 0) + 1;
        return acc;
      }, {});

    return Object.keys(eachResortsCount).map(resortCode => {
      return hotels
        .flatMap(hotel => hotel.resorts)
        .find(resort => resort.resort_code === resortCode);
    });
  };

  const findRoomWithMinPrice = (rooms: YadoRoom[]) => {
    return rooms.reduce((min, room) => {
      return Math.min(...roomPlanPrices(room)) <
        Math.min(...roomPlanPrices(min))
        ? room
        : min;
    }, rooms[0]);
  };

  const findRoomWithMaxPrice = (rooms: YadoRoom[]) => {
    return rooms.reduce((max, room) => {
      return Math.max(...roomPlanPrices(room)) >
        Math.max(...roomPlanPrices(max))
        ? room
        : max;
    }, rooms[0]);
  };

  const searchTitle = () => {
    if (query.forHoshinoResort) return i18n.t('stay_packages.hotels.index.search_results_for_area', { area: `${i18n.t('stay_packages.home.hoshino_resort')} ` } );
    if (fetchedQuery.areaCodes.length === 1) return i18n.t('stay_packages.hotels.index.search_results_for_area', { area: i18n.t(`areas.${fetchedQuery.areaCodes[0]}`) } )

    return i18n.t('searches.index.search_result')
  }

  return (
    <>
      <BreadCrumb ancestors={[{ text: i18n.t('stay_packages.hotels.index.hotel_plus_lift_pass'), path: '/snow/stay-packages' }]} descendant={i18n.t('searches.index.search_result')} />
      <div className="mx-0 p-3 mb-4 d-flex flex-column" style={{ backgroundColor: '#E6F3FF' }}>
        <div className="container px-0">
          <div className="row justify-content-start justify-content-lg-center mb-3">
            <div className={`col-12 col-lg-auto ${styles.promotionText} mb-2 mb-sm-0`}>
              <img src={getImage(checkPointSvg)} alt='' className="mr-1" />
              <p className="m-0">
                {i18n.t('stay_packages.hotels.index.up_to_30_percent_off')}
              </p>
            </div>
            <div className={`col-12 col-lg-auto ${styles.promotionText} mb-2 mb-sm-0`}>
              <img src={getImage(checkPointSvg)} alt='' className="mr-1" />
              <p className="m-0">
                {i18n.t('stay_packages.hotels.index.we_offer_free_cancellation')}
              </p>
            </div>
            <div className={`col-12 col-lg-auto ${styles.promotionText} mb-2 mb-sm-0`}>
              <img src={getImage(checkPointSvg)} alt='' className="mr-1" />
              <p className="m-0">
                {i18n.t('stay_packages.hotels.index.featured_hotels_within_5_minute_to_ski_resort')}
              </p>
            </div>
          </div>
          <div className="row m-auto align-items-center justify-content-between">
            <SearchForm
              query={query}
              featuredAreaCodes={featuredAreaCodes}
              otherAreaCodes={otherAreaCodes}
              minDate={startOfDay(today)}
              withResortForm={true}
              handleQueryChange={newQuery => setQuery(newQuery)}
            />
            <button
              type="submit"
              onClick={() => updateHotels()}
              className="col-12 col-md-3 mt-2 mt-md-0 btn btn-primary rounded-pill font-weight-bold flex-shrink-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                i18n.t('components.accommodation.search_form.search')
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="row container mx-auto">
        <div className="col-12 col-md-3 d-none d-md-block">
          <SearchFormFiltering
            featuredAreaCodes={featuredAreaCodes}
            query={query}
            onChangeQuery={(query) => handleOnChangeFiltering(query)}
          />
        </div>
        <div className="col-12 col-lg-9 px-0">
          <ResortFilteringBar
            resorts={relatedResorts}
            selectedResortCodes={query.resortCodes}
            handleChangeResortCodes={codes => {
              setQuery({ ...query, resortCodes: codes });
            }}
          />
          <div className="d-md-none">
            <SearchFormFilteringSp
              featuredAreaCodes={featuredAreaCodes}
              query={query}
              onChangeQuery={(query) => handleOnChangeFiltering(query)}
            />
          </div>
          <div className={styles.searchHeader}>
            <h1>{searchTitle()}</h1>
            <span style={{minWidth: 'fit-content'}}>
              {hotels.length === 1
                ? `(${i18n.t('helpers.page_entries_info.one_page.display_entries.one')})`
                : `(${i18n.t('helpers.page_entries_info.one_page.display_entries.other', {
                    count: hotels.length,
                  })})`}
            </span>
          </div>
          <div className="d-flex justify-content-end mb-3">
            <SelectBoxSort handleSortChange={sortBy => setSortBy(sortBy)} />
          </div>
          {!isLoading && searchInitiated && hotels.length === 0 ? (
            <div className="text-center my-5">
              <img src={getImage(treeSvg)} alt='' className="mr-1" height={145} />
              <h2 className="mt-5 h4 font-weight-bold">{i18n.t('stay_packages.hotels.index.we_are_continuously_adding_new_hotels_and_special_plans')}</h2>
              <p style={{whiteSpace: 'pre-wrap'}}>{i18n.t('stay_packages.hotels.index.at_the_moment_no_rooms_or_plans_matching_your_search_criteria_were_found')}</p>
            </div>
          ) : (
            <HotelList hotels={hotels} query={query} fetchedQuery={fetchedQuery} sortBy={sortBy} onClickArea={handleClickHotelCardArea} />
          )}
        </div>
      </div>
      <div className="container">
        <div dangerouslySetInnerHTML={{ __html: recommendedHotelsHTML }} />
      </div>
    </>
  );
};

export default HotelsSearch;
